/**
 * Opens a Google OAuth popup window and waits for the OAuth callback
 * @param oauthUrl The Google OAuth URL to open in the popup
 * @returns Promise that resolves with the access token or rejects with an error
 */
export const openGoogleOAuthPopup = (oauthUrl: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		// Calculate popup window position (centered on screen)
		const width = 500;
		const height = 600;
		const left = window.screenX + (window.outerWidth - width) / 2;
		const top = window.screenY + (window.outerHeight - height) / 2;

		// Open popup window
		const popup = window.open(
			oauthUrl,
			'Google Sign In',
			`width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
		);

		if (!popup) {
			reject(new Error('Failed to open popup window. Please allow popups for this site.'));
			return;
		}

		// Listen for messages from the popup
		const messageHandler = (event: MessageEvent) => {
			// In production, verify event.origin for security
			// if (event.origin !== 'https://localhost:7062') return;

			if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
				window.removeEventListener('message', messageHandler);
				clearInterval(pollInterval);
				if (!popup.closed) {
					popup.close();
				}
				resolve(event.data.accessToken);
			} else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
				window.removeEventListener('message', messageHandler);
				clearInterval(pollInterval);
				if (!popup.closed) {
					popup.close();
				}
				reject(new Error(event.data.error || 'Google authentication failed'));
			}
		};

		window.addEventListener('message', messageHandler);

		// Poll to detect if popup was closed manually
		const pollInterval = setInterval(() => {
			if (popup.closed) {
				clearInterval(pollInterval);
				window.removeEventListener('message', messageHandler);
				reject(new Error('Authentication was cancelled'));
			}
		}, 500);

		// Timeout after 5 minutes
		setTimeout(() => {
			if (!popup.closed) {
				clearInterval(pollInterval);
				window.removeEventListener('message', messageHandler);
				popup.close();
				reject(new Error('Authentication timeout'));
			}
		}, 5 * 60 * 1000);
	});
};

